// src/components/NotificationsCard.jsx — Activation des notifications push (Web Push)
// Affiché dans Réglages → Connexions. Gère permission + abonnement service worker.
//
// macOS : les notifications ne marchent QUE depuis l'app ajoutée au Dock
// (Safari « Fichier → Ajouter au Dock »), pas dans un onglet Safari classique.
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Check } from 'lucide-react';
import { TYPO, S, SHADCN, R, softTone } from '../theme';
import Card from './Card';
import Button from './Button';

const SUPPORTED = typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator
  && typeof window !== 'undefined'
  && 'PushManager' in window
  && 'Notification' in window;

// Clé VAPID base64url → Uint8Array (format attendu par pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export default function NotificationsCard() {
  const [perm, setPerm]       = useState(SUPPORTED ? Notification.permission : 'unsupported');
  const [subscribed, setSub]  = useState(false);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');

  // État initial : y a-t-il déjà un abonnement ?
  useEffect(() => {
    if (!SUPPORTED) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSub(!!sub))
      .catch(() => {});
  }, []);

  const enable = useCallback(async () => {
    setBusy(true); setMsg('');
    try {
      const permission = await Notification.requestPermission();
      setPerm(permission);
      if (permission !== 'granted') {
        setMsg('Permission refusée — autorise les notifications dans les réglages système.');
        return;
      }

      // Clé publique VAPID depuis le serveur
      const cfgRes = await fetch('/api/supabase-data?resource=push-config', { credentials: 'same-origin' });
      const cfg = await cfgRes.json();
      if (!cfg.configured || !cfg.vapidPublicKey) {
        setMsg('Notifications non configurées côté serveur (clés VAPID manquantes).');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(cfg.vapidPublicKey),
      });

      const res = await fetch('/api/supabase-data?resource=push-subscribe', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub),
      });
      if (!res.ok) throw new Error('enregistrement abonnement échoué');

      setSub(true);
      setMsg('Notifications activées ✓');
    } catch (e) {
      setMsg(`Erreur : ${e.message}`);
    } finally {
      setBusy(false);
    }
  }, []);

  const disable = useCallback(async () => {
    setBusy(true); setMsg('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch('/api/supabase-data?resource=push-subscribe', {
          method: 'DELETE',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setSub(false);
      setMsg('Notifications désactivées.');
    } catch (e) {
      setMsg(`Erreur : ${e.message}`);
    } finally {
      setBusy(false);
    }
  }, []);

  const titleStyle = { ...TYPO.h3, color: SHADCN.fg, margin: 0 };

  return (
    <Card style={{ marginTop: S[4] }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: S[3] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: S[3] }}>
          <div style={{
            width: 36, height: 36, borderRadius: R.md, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: subscribed ? softTone(SHADCN.success) : SHADCN.muted,
            color: subscribed ? SHADCN.success : SHADCN.mutedFg,
          }}>
            {subscribed ? <Bell size={18} strokeWidth={2.2} /> : <BellOff size={18} strokeWidth={2.2} />}
          </div>
          <div>
            <div style={titleStyle}>Notifications</div>
            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: 2 }}>
              Factures, échéances, trésorerie, todos, point mort
            </div>
          </div>
        </div>

        {SUPPORTED ? (
          subscribed ? (
            <Button variant="ghost" size="sm" disabled={busy} onClick={disable}>
              Désactiver
            </Button>
          ) : (
            <Button variant="primary" size="sm" disabled={busy} onClick={enable}
              leftIcon={<Bell size={14} strokeWidth={2.4} />}>
              {busy ? 'Activation…' : 'Activer'}
            </Button>
          )
        ) : null}
      </div>

      {subscribed && (
        <div style={{ ...TYPO.caption, color: SHADCN.success, marginTop: S[3], display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={13} strokeWidth={2.6} /> Cet appareil reçoit les notifications.
        </div>
      )}

      {!SUPPORTED && (
        <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[3], lineHeight: 1.5 }}>
          Cet appareil/navigateur ne supporte pas les notifications.<br />
          Sur Mac : ouvre l'app depuis le Dock (Safari → « Fichier → Ajouter au Dock »), pas dans un onglet.
        </div>
      )}

      {msg && (
        <div style={{ ...TYPO.caption, color: msg.startsWith('Erreur') ? SHADCN.error : SHADCN.mutedFg, marginTop: S[3] }}>
          {msg}
        </div>
      )}
    </Card>
  );
}
